import { useState } from 'react';
import { Anchor, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Calendar, Repeat } from 'react-feather';
import { BannerCallout } from '../preSessionReflection/BannerCallout';
import { BANNER_GREEN } from '../preSessionReflection/bannerTones';
import {
  peerLeaderSessionPrepApi,
  type SessionLeaderRsvpStatus,
} from '../../../services/api/peerLeaderSessionPrep';

interface PeerLeaderContextBannerProps {
  sessionId: string;
  sessionNumber: number | null;
  /** Pre-formatted "Wed, Mar 21" or similar — caller handles localization. */
  sessionDateLabel: string;
  /** Resolved CM full name, or null → falls back to "your Cohort Manager". */
  cmName: string | null;
  /** Current RSVP status — drives which banner variant renders. */
  rsvpStatus: SessionLeaderRsvpStatus;
  /** Called after a successful RSVP write. The page refetches / routes. */
  onRsvpUpdated: (status: 'confirmed' | 'declined') => void;
}

/**
 * MED-1049 (E4.2b) Card 1 — single context banner for the unified peer-leader
 * pre-session page. Merges the three prior surfaces (PeerLeaderBanner,
 * RsvpConfirmationCard, DualRoleBanner) into ONE banner that swings on
 * `rsvp_status` so there is exactly one banner instead of the prior two:
 *
 *   • `unconfirmed` → RSVP request: "Can you lead {Session N} on {date}?" with
 *     encouragement and [Yes, I'll be there] / [No, something came up]. (AC4)
 *   • `confirmed`   → quieter dual-role message + a "Something came up?"
 *     affordance for a late decline. (AC4)
 *
 * Both decline paths route through a confirmation modal before calling the
 * existing MED-905 endpoint with `status: 'declined'` — no new backend (AC5).
 * On a successful decline the parent reuses the existing decline routing
 * (redirect to the participant portal). `confirm`/`decline` reuse
 * `peerLeaderSessionPrepApi.setRsvp`, the same wrapper the old card used.
 */
export function PeerLeaderContextBanner({
  sessionId,
  sessionNumber,
  sessionDateLabel,
  cmName,
  rsvpStatus,
  onRsvpUpdated,
}: PeerLeaderContextBannerProps) {
  const [submitting, setSubmitting] = useState<null | 'confirmed' | 'declined'>(
    null
  );
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  const sessionLabel = sessionNumber
    ? `Session ${sessionNumber}`
    : 'this session';
  const cmLabel = cmName ?? 'your Cohort Manager';

  const writeRsvp = async (status: 'confirmed' | 'declined') => {
    setSubmitting(status);
    try {
      await peerLeaderSessionPrepApi.setRsvp(sessionId, status);
      if (status === 'confirmed') {
        notifications.show({
          color: 'medleyGreen',
          title: 'Confirmed',
          message: sessionNumber
            ? `You're confirmed for Session ${sessionNumber}!`
            : "You're confirmed!",
        });
      } else {
        notifications.show({
          color: 'medleyGreen',
          title: 'Thanks',
          message: `Thanks for letting us know. ${cmLabel} has been notified.`,
        });
      }
      onRsvpUpdated(status);
    } catch (err) {
      notifications.show({
        color: 'dangerRed',
        title: 'Something went wrong',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSubmitting(null);
      setDeclineModalOpen(false);
    }
  };

  const declineModal = (
    <Modal
      opened={declineModalOpen}
      onClose={() => setDeclineModalOpen(false)}
      title="Let us know you can't lead?"
      centered
    >
      <Stack gap="md">
        <Text size="sm">
          We&apos;ll let {cmLabel} know right away so they can find another
          leader for {sessionLabel}. You&apos;ll still attend as a participant.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            disabled={submitting !== null}
            onClick={() => setDeclineModalOpen(false)}
          >
            Never mind
          </Button>
          <Button
            color="dangerRed"
            loading={submitting === 'declined'}
            onClick={() => void writeRsvp('declined')}
          >
            Yes, something came up
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  // --- unconfirmed: RSVP request -------------------------------------------
  if (rsvpStatus === 'unconfirmed') {
    return (
      <BannerCallout
        tone={BANNER_GREEN}
        icon={Calendar}
        title={`Can you lead ${sessionLabel} on ${sessionDateLabel}?`}
        subtitle="You've been asked to facilitate this session. Medley has prepared the agenda and facilitation notes for you — confirm below and review your prep."
      >
        <Group gap="sm" wrap="wrap">
          <Button
            color="medleyGreen"
            loading={submitting === 'confirmed'}
            disabled={submitting !== null}
            onClick={() => void writeRsvp('confirmed')}
          >
            Yes, I&apos;ll be there
          </Button>
          <Button
            variant="default"
            disabled={submitting !== null}
            onClick={() => setDeclineModalOpen(true)}
          >
            No, something came up
          </Button>
        </Group>
        {declineModal}
      </BannerCallout>
    );
  }

  // --- confirmed (or any non-unconfirmed state): dual-role message ----------
  return (
    <BannerCallout
      tone={BANNER_GREEN}
      icon={Repeat}
      title="You're both facilitating and participating today"
      subtitle="Medley has prepared facilitation notes and the agenda for you. Review your prep below, then start the session when you're ready."
    >
      <Anchor
        component="button"
        type="button"
        size="sm"
        c="dimmed"
        underline="always"
        onClick={() => setDeclineModalOpen(true)}
        style={{ alignSelf: 'flex-start' }}
      >
        Something came up?
      </Anchor>
      {declineModal}
    </BannerCallout>
  );
}

export default PeerLeaderContextBanner;
